import {
  ALLOWED_TYPES,
  ALLOWED_EXTENSIONS,
  MAX_FILE_SIZE_GB,
  MAX_FILES,
  MAX_TOTAL_SIZE_GB,
  FileWithMetadata,
} from "./documentService";

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

export interface FilesValidationResult {
  validFiles: File[];
  errors: string[];
}

// Secure UUID generator function
export const generateUUID = (): string => {
  return crypto.randomUUID();
};

// Format file size utility
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Validate a single file
export const validateFile = (file: File): FileValidationResult => {
  const mimeType = (file.type || "").toLowerCase();
  const extension = (file.name.split(".").pop() || "").toLowerCase();
  const fileExtension = "." + extension;

  const allowedExtensionsForType = ALLOWED_TYPES[mimeType];
  const isMimeValid =
    allowedExtensionsForType && allowedExtensionsForType.includes(extension);

  const allAllowedExtensions = Object.values(ALLOWED_TYPES).flat();
  const isExtensionValid = allAllowedExtensions.includes(extension);

  if (!isMimeValid && !isExtensionValid) {
    return {
      isValid: false,
      error: `${file.name} - Invalid file type. Only PDF, DOC, DOCX, XLS, XLSX, TXT, HEIC, HEIF, JPG, JPEG, PNG, GIF, TIF, TIFF, XML, XMLS, WMV, MP4, AVI, MPEG, ZIP, and HTML are allowed.`,
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE_GB * 1024 * 1024 * 1024) {
    return {
      isValid: false,
      error: `${file.name} - File too large. Maximum size is ${MAX_FILE_SIZE_GB}GB.`,
    };
  }

  // Check file extension (additional security)
  if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
    return {
      isValid: false,
      error: `${file.name} - File extension '${fileExtension}' is not allowed.`,
    };
  }

  return { isValid: true };
};

// Validate multiple files with existing files context
export const validateFiles = (
  newFiles: FileList,
  existingFiles: FileWithMetadata[]
): FilesValidationResult => {
  const newFilesArray = Array.from(newFiles);
  const validFiles: File[] = [];
  const errors: string[] = [];

  // Check file count limit
  if (existingFiles.length + newFilesArray.length > MAX_FILES) {
    errors.push(
      `Maximum ${MAX_FILES} files allowed. You can only add ${
        MAX_FILES - existingFiles.length
      } more files.`
    );
    return { validFiles: [], errors };
  }

  // Check total size limit
  const totalNewSize = newFilesArray.reduce((acc, file) => acc + file.size, 0);
  const currentTotalSize = existingFiles.reduce(
    (acc, fileWithMeta) => acc + fileWithMeta.file.size,
    0
  );

  if (
    currentTotalSize + totalNewSize >
    MAX_TOTAL_SIZE_GB * 1024 * 1024 * 1024
  ) {
    errors.push(
      `Total size exceeds ${MAX_TOTAL_SIZE_GB}GB limit. Please select smaller files or fewer files.`
    );
    return { validFiles: [], errors };
  }

  // Validate individual files
  for (const file of newFilesArray) {
    const validation = validateFile(file);

    if (!validation.isValid && validation.error) {
      errors.push(validation.error);
      continue;
    }

    // Check for duplicate files
    const isDuplicate = existingFiles.some(
      (existingFile) =>
        existingFile.file.name === file.name &&
        existingFile.file.size === file.size &&
        existingFile.file.lastModified === file.lastModified
    );

    if (isDuplicate) {
      errors.push(`${file.name} - File already added.`);
      continue;
    }

    validFiles.push(file);
  }

  return { validFiles, errors };
};

// Validate document metadata
export interface MetadataValidationResult {
  isValid: boolean;
  errors: string[];
}

export const validateMetadata = (
  tailNumber: string,
  documentCategory: string,
  serialNumber: string,
  aircraftType: string,
  documentTitle: string,
  description: string,
  isSerialNumberRequired: boolean = false
): MetadataValidationResult => {
  const errors: string[] = [];

  if (!tailNumber.trim()) {
    errors.push("Please enter a flight tail number");
  }

  if (!documentCategory) {
    errors.push("Please select a document category");
  }

  if (isSerialNumberRequired && !serialNumber.trim()) {
    errors.push("Please enter a serial number");
  }

  // Aircraft type validation disabled - field is commented out
  // if (!aircraftType.trim()) {
  //   errors.push("Please enter an aircraft type");
  // }

  if (!documentTitle.trim()) {
    errors.push("Please enter a document title");
  }

  if (!description.trim()) {
    errors.push("Please enter a document description");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
